// src/modules/estimateDetails/EstimateDetailList.js
import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { FaEdit, FaTrash, FaPlus, FaSearch, FaInbox, FaList, FaSort, FaSortAlphaDown, FaSortAlphaUp } from 'react-icons/fa';
import DashboardLayout from '../../layouts/DashboardLayout';
import Input from '../../components/shared/Input';
import Button from '../../components/shared/Button';
import Pagination from '../../components/shared/Pagination';
import Modal from '../../components/shared/Modal';
import { getEstimateDetails, deleteEstimateDetail } from './EstimateDetailService';
import { ResponsiveHeader, StatusBadge } from '../../components/styled/CommonStyles';
import {
  GridContainer,
  Card,
  CardHeader,
  CardContent,
  InfoRow,
  ActionIconsContainer,
  ActionButton,
  EmptyState,
  FilterSection,
  FilterGroup,
  FilterLabel,
  FilterButton
} from '../../components/styled/GridComponents';

const EstimateDetailList = () => {
  const [details, setDetails] = useState([]); // All Estimate Details
  const [filteredDetails, setFilteredDetails] = useState([]); // Filtered Details based on search
  const [searchQuery, setSearchQuery] = useState(''); // Search query state
  const [currentPage, setCurrentPage] = useState(1); // Current page for pagination
  const [itemsPerPage] = useState(5); // Number of items per page
  const [deleteModalOpen, setDeleteModalOpen] = useState(false); // State for delete confirmation modal
  const [selectedDetail, setSelectedDetail] = useState(null); // Detail selected for deletion
  const [sortConfig, setSortConfig] = useState({ field: 'name', direction: 'asc' });

  const navigate = useNavigate();

  useEffect(() => {
    // Fetch all estimate details when component mounts
    const fetchDetails = async () => {
      try {
        const response = await getEstimateDetails();
        setDetails(response || []); // Set details state with response data or an empty array
        setFilteredDetails(response || []); // Initialize filteredDetails with all details
      } catch (error) {
        console.error('Error fetching estimate details:', error);
      }
    };
    fetchDetails();
  }, []);

  useEffect(() => {
    // Update filtered details whenever search query or details change
    const filtered = details.filter(
      (detail) =>
        detail.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        detail.description.toLowerCase().includes(searchQuery.toLowerCase())
    );
    setFilteredDetails(filtered);
    setCurrentPage(1); // Reset to the first page when search query changes
  }, [searchQuery, details]);

  const handleDelete = async () => {
    try {
      await deleteEstimateDetail(selectedDetail.id);
      const updatedDetails = details.filter((detail) => detail.id !== selectedDetail.id);
      setDetails(updatedDetails);
      setFilteredDetails(updatedDetails);
      setDeleteModalOpen(false); // Close modal on successful delete
    } catch (error) {
      console.error('Failed to delete estimate detail:', error);
    }
  };

  // Open the delete confirmation modal
  const openDeleteModal = (detail) => {
    setSelectedDetail(detail);
    setDeleteModalOpen(true);
  };

  // Close the delete confirmation modal
  const closeDeleteModal = () => {
    setSelectedDetail(null);
    setDeleteModalOpen(false);
  };

  // Pagination calculations
  const indexOfLastDetail = currentPage * itemsPerPage;
  const indexOfFirstDetail = indexOfLastDetail - itemsPerPage;
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

 const filteredAndSortedLeads = useMemo(() => {
    let filtered = details.filter((extra) => {
      const matchesSearch = extra.name.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesSearch;
    });

    return filtered.sort((a, b) => {
      let aValue, bValue;
      
      switch (sortConfig.field) {
        case 'name':
          aValue = a.name;
          bValue = b.name;
          break;
        default:
          aValue = a[sortConfig.field];
          bValue = b[sortConfig.field];
      }

      if (sortConfig.direction === 'asc') {
        return aValue.localeCompare(bValue);
      } else {
        return bValue.localeCompare(aValue);
      }
    });
  }, [details, searchQuery, sortConfig]);

  const currentDetails = filteredAndSortedLeads.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleSort = (field) => {
    setSortConfig(prevConfig => ({
      field,
      direction: prevConfig.field === field && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  return (
    <DashboardLayout>
      <ResponsiveHeader
        title="Job Extras"
        onSearch={setSearchQuery}
        onAdd={() => navigate('/estimates/detail/new')}
      />

      <FilterSection>
        <FilterGroup>
          <FilterLabel>Sort by:</FilterLabel>
          <FilterButton 
            isActive={sortConfig.field === 'name'}
            onClick={() => handleSort('name')}
          >
            {sortConfig.field === 'name' ? 
              (sortConfig.direction === 'asc' ? <FaSortAlphaDown /> : <FaSortAlphaUp />) 
              : <FaSort />
            }
            Name
          </FilterButton>
        </FilterGroup>
      </FilterSection>

      <GridContainer>
      {currentDetails.length === 0 ? (
        <EmptyState>
          <FaInbox size={48} />
          <p>No estimates found. Create a new estimate to get started.</p>
        </EmptyState>
      ) : (
        currentDetails.map((detail) => (
          <Card key={detail.id}>
            <CardHeader>
                <h3>{detail.name}</h3>
              </CardHeader>
              <CardContent>
              <InfoRow>
                  <FaList />
                  {detail.description ? (
                  <span>{detail.description}</span>
                  ) : (<span>No Description</span>)
                  }
                </InfoRow>
              </CardContent>
              <ActionIconsContainer>
              <ActionButton
                  onClick={() => navigate(`/estimates/detail/edit/${detail.id}`)}
                  title="Edit Extra"
                >
                  <FaEdit />
                </ActionButton>
              <ActionButton
                  onClick={() => {
                    openDeleteModal(detail);
                    setDeleteModalOpen(true);
                  }}
                  title="Delete Estimate"
                >
                  <FaTrash />
                </ActionButton>
                </ActionIconsContainer>
          </Card>
        )
      )
    )}
    </GridContainer>

        {/* Pagination */}
        <Pagination
          itemsPerPage={itemsPerPage}
          totalItems={filteredDetails.length}
          paginate={paginate}
          currentPage={currentPage}
        />

        {/* Delete Confirmation Modal */}
        {deleteModalOpen && (
          <Modal
            isOpen={deleteModalOpen}
            onConfirm={handleDelete}
            onCancel={closeDeleteModal}
            title="Confirm Delete"
          >
            Are you sure you want to delete the estimate detail "{selectedDetail?.name}"?
          </Modal>
        )}
    </DashboardLayout>
  );
};

export default EstimateDetailList;
